import React, { useState, useEffect } from "react";
import { db, storage } from "../../firebase";
import { 
    collection, getDocs, addDoc, updateDoc, deleteDoc, 
    doc, serverTimestamp, orderBy, query 
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { FiPlus, FiEdit2, FiTrash2, FiX, FiUpload, FiMapPin, FiSearch, FiLink, FiPhone } from "react-icons/fi";
import toast from "react-hot-toast";

const EMPTY_FORM = {
    name: "",
    logoUrl: "",
    address: "",
    phone: "",
    webpage: "",
    productIds: []
};

export default function AdminBookstores() {
    const [bookstores, setBookstores] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [bookSearch, setBookSearch] = useState("");

    async function loadData() {
        setLoading(true);
        try {
            // Fetch from both collections and merge
            const [bookSnap, libSnap] = await Promise.all([
                getDocs(query(collection(db, "bookstores"), orderBy("createdAt", "desc"))),
                getDocs(query(collection(db, "libraries"), orderBy("createdAt", "desc")))
            ]);

            const combined = [
                ...bookSnap.docs.map(d => ({ ...d.data(), id: d.id, source: "bookstores" })),
                ...libSnap.docs.map(d => ({ ...d.data(), id: d.id, source: "libraries" }))
            ];

            // Deduplicate by ID and sort by createdAt
            const unique = combined
                .filter((v, i, a) => a.findIndex(t => t.id === v.id) === i)
                .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));

            setBookstores(unique);

            const prodSnap = await getDocs(query(collection(db, "products"), orderBy("title", "asc")));
            setProducts(prodSnap.docs.map(d => ({ ...d.data(), id: d.id })));
        } catch (e) {
            toast.error("Could not load data.");
            console.error(e);
        }
        setLoading(false);
    }

    useEffect(() => { loadData(); }, []);

    const searchedBooks = bookSearch.trim()
        ? products.filter(p => p.title.toLowerCase().includes(bookSearch.toLowerCase()))
        : products;

    function openAdd() {
        setForm(EMPTY_FORM);
        setEditId(null);
        setModal(true);
    }

    function openEdit(store) {
        setForm({ ...store });
        setEditId(store.id);
        setModal(true);
    }

    async function handleLogoUpload(e) {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        const storageRef = ref(storage, `bookstores/${Date.now()}_${file.name}`);
        const uploadTask = uploadBytesResumable(storageRef, file);

        uploadTask.on("state_changed", null, 
            (error) => {
                toast.error("Upload failed");
                setUploading(false);
            }, 
            async () => {
                const url = await getDownloadURL(uploadTask.snapshot.ref);
                setForm(f => ({ ...f, logoUrl: url }));
                setUploading(false);
                toast.success("Logo uploaded!");
            }
        );
    }

    async function handleSave() {
        if (!form.name || !form.address) {
            toast.error("Name and address are required.");
            return;
        }
        setSaving(true);
        try {
            // Remove source from data to save
            const { source, ...dataToSave } = form;
            const data = { ...dataToSave, updatedAt: serverTimestamp() };
            
            if (editId) {
                const col = source === "libraries" ? "libraries" : "bookstores";
                await updateDoc(doc(db, col, editId), data);
                toast.success("Bookstore updated!");
            } else {
                await addDoc(collection(db, "bookstores"), { ...data, createdAt: serverTimestamp() });
                toast.success("Bookstore added!");
            }
            setModal(false);
            loadData();
        } catch (e) {
            toast.error("Error saving bookstore.");
        }
        setSaving(false);
    }

    async function handleDelete(id, source) {
        if (!window.confirm("Are you sure?")) return;
        try {
            // Delete from the correct collection
            const col = source === "libraries" ? "libraries" : "bookstores";
            await deleteDoc(doc(db, col, id));
            toast.success("Bookstore deleted.");
            loadData();
        } catch (e) {
            toast.error("Error deleting.");
        }
    }

    function toggleProduct(productId) {
        setForm(f => {
            const productIds = f.productIds || [];
            if (productIds.includes(productId)) {
                return { ...f, productIds: productIds.filter(id => id !== productId) };
            } else {
                return { ...f, productIds: [...productIds, productId] };
            }
        });
    }

    if (loading) return <div className="admin-loading">Loading bookstores...</div>;

    return (
        <div className="admin-section">
            <div className="admin-section-header">
                <h3>Local Bookstores</h3>
                <button className="admin-btn-primary" onClick={openAdd}>
                    <FiPlus /> Add Bookstore
                </button>
            </div>

            <div className="admin-card">
            <div className="admin-table-wrap">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>Logo</th>
                            <th>Name</th>
                            <th>Address</th>
                            <th>Books</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {bookstores.map(store => (
                            <tr key={store.id}>
                                <td>
                                    {store.logoUrl ? (
                                        <img src={store.logoUrl} alt={store.name} style={{ width: 40, height: 40, borderRadius: 6, objectFit: "cover" }} />
                                    ) : (
                                        <div style={{ width: 40, height: 40, background: "#eee", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                            <FiMapPin color="#ccc" />
                                        </div>
                                    )}
                                </td>
                                <td style={{ fontWeight: 600 }}>{store.name}</td>
                                <td style={{ fontSize: 13, color: "#666" }}>{store.address}</td>
                                <td>
                                    <span className="admin-badge">{(store.productIds || []).length} Products</span>
                                </td>
                                <td>
                                    <div style={{ display: "flex", gap: 8 }}>
                                        <button className="admin-action-btn edit" onClick={() => openEdit(store)}><FiEdit2 /></button>
                                        <button className="admin-action-btn delete" onClick={() => handleDelete(store.id, store.source)}><FiTrash2 /></button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            </div>

            {modal && (
                <div className="admin-modal-backdrop" onClick={e => e.target === e.currentTarget && setModal(false)}>
                    <div className="admin-modal" style={{ maxWidth: 800 }}>
                        <div className="admin-modal-header">
                            <h3>{editId ? "Edit Bookstore" : "Add New Bookstore"}</h3>
                            <button className="admin-modal-close" onClick={() => setModal(false)}><FiX size={20} /></button>
                        </div>
                        <div className="admin-modal-body" style={{ maxHeight: "80vh", overflowY: "auto" }}>
                            <div className="admin-grid-2" style={{ gap: 20 }}>
                                <div className="admin-form-field full">
                                    <label>Bookstore Name *</label>
                                    <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Downtown Books & Coffee" />
                                </div>
                                <div className="admin-form-field">
                                    <label>Address *</label>
                                    <input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="123 Main St, City, State" />
                                </div>
                                <div className="admin-form-field">
                                    <label>Phone (Optional)</label>
                                    <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="555-0199" />
                                </div>
                                <div className="admin-form-field full">
                                    <label>Website URL</label>
                                    <input value={form.webpage} onChange={e => setForm({ ...form, webpage: e.target.value })} placeholder="https://bookstore.example.com" />
                                </div>

                                <div className="admin-form-field full">
                                    <label>Bookstore Logo</label>
                                    <div style={{ display: "flex", alignItems: "center", gap: 15 }}>
                                        {form.logoUrl && <img src={form.logoUrl} alt="Logo Preview" style={{ width: 60, height: 60, borderRadius: 10, objectFit: "cover", border: "2px solid #eee" }} />}
                                        <label className="admin-upload-btn" style={{ cursor: uploading ? "wait" : "pointer" }}>
                                            <FiUpload /> {uploading ? "Uploading..." : "Upload Logo"}
                                            <input type="file" onChange={handleLogoUpload} style={{ display: "none" }} disabled={uploading} />
                                        </label>
                                    </div>
                                </div>

                                <div className="admin-form-field full">
                                    <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        📚 Products available in this Bookstore
                                        <span style={{ fontWeight: 400, color: "#888", fontSize: 12 }}>({(form.productIds || []).length} selected)</span>
                                    </label>
                                    
                                    <div style={{ marginBottom: 10, position: "relative" }}>
                                        <FiSearch style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#aaa" }} />
                                        <input 
                                            value={bookSearch} 
                                            onChange={e => setBookSearch(e.target.value)} 
                                            placeholder="Search products to attach..." 
                                            style={{ paddingLeft: 36 }}
                                        />
                                    </div>

                                    <div style={{ 
                                        maxHeight: 250, overflowY: "auto", border: "1.5px solid #e0ddd8", 
                                        borderRadius: 12, padding: 8, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 
                                    }}>
                                        {searchedBooks.map(p => {
                                            const isSelected = (form.productIds || []).includes(p.id);
                                            return (
                                                <div 
                                                    key={p.id} 
                                                    onClick={() => toggleProduct(p.id)}
                                                    style={{
                                                        display: "flex", alignItems: "center", gap: 10, padding: "8px 12px",
                                                        borderRadius: 8, cursor: "pointer", border: isSelected ? "1.5px solid var(--green-primary)" : "1.5px solid transparent",
                                                        background: isSelected ? "rgba(13, 74, 53, 0.05)" : "transparent",
                                                        transition: "all 0.2s"
                                                    }}
                                                >
                                                    <img src={p.imageUrl} alt="" style={{ width: 30, height: 40, objectFit: "cover", borderRadius: 4 }} />
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.title}</div>
                                                        <div style={{ fontSize: 11, color: "#888" }}>{p.author}</div>
                                                    </div>
                                                    {isSelected && <span style={{ color: "var(--green-primary)", fontWeight: 800 }}>✓</span>}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="admin-modal-footer">
                            <button className="admin-btn-secondary" onClick={() => setModal(false)}>Cancel</button>
                            <button className="admin-btn-primary" onClick={handleSave} disabled={saving || uploading}>
                                {saving ? "Saving..." : editId ? "Update Bookstore" : "Add Bookstore"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .admin-badge {
                    background: #f0f7ff;
                    color: #1565c0;
                    padding: 4px 10px;
                    border-radius: 100px;
                    font-size: 11px;
                    font-weight: 700;
                    text-transform: uppercase;
                }
                .admin-upload-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    background: #f8f9fa;
                    border: 1.5px dashed #ccc;
                    padding: 10px 20px;
                    border-radius: 10px;
                    font-size: 13px;
                    font-weight: 600;
                    color: #666;
                }
            `}</style>
        </div>
    );
}
